/**
 * Utilidades para manejo de fechas y tiempos
 */

/**
 * Formatea una fecha ISO a formato legible en español
 * @param {string} isoDate - Fecha en formato ISO
 * @returns {string} Fecha formateada (ej: "01/04/2026 09:00")
 */
export const formatDate = (isoDate) => {
  if (!isoDate) return "-";
  const date = new Date(isoDate);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Calcula el tiempo restante desde ahora hasta una fecha futura
 * @param {string} futureDate - Fecha futura en formato ISO
 * @returns {object} Objeto con días, horas, minutos y texto legible
 */
export const getTimeRemaining = (futureDate) => {
  if (!futureDate) return null;

  const now = new Date();
  const target = new Date(futureDate);
  const diff = target - now;

  // Si la fecha ya pasó
  if (diff <= 0) {
    return {
      isPast: true,
      days: 0,
      hours: 0,
      minutes: 0,
      text: "Atrasado",
      shortText: "Atrasado",
    };
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  // Generar texto legible
  let text = "";
  let shortText = "";

  if (days > 365) {
    const years = Math.floor(days / 365);
    text = `En ${years} año${years > 1 ? "s" : ""}`;
    shortText = `${years}a`;
  } else if (days > 30) {
    const months = Math.floor(days / 30);
    text = `En ${months} mes${months > 1 ? "es" : ""}`;
    shortText = `${months}m`;
  } else if (days > 0) {
    text = `En ${days} día${days > 1 ? "s" : ""}`;
    shortText = `${days}d`;
  } else if (hours > 0) {
    text = `En ${hours} hora${hours > 1 ? "s" : ""}`;
    shortText = `${hours}h`;
  } else {
    text = `En ${minutes} minuto${minutes > 1 ? "s" : ""}`;
    shortText = `${minutes}min`;
  }

  return {
    isPast: false,
    days,
    hours,
    minutes,
    text,
    shortText,
  };
};

/**
 * Parsea una expresión cron a texto legible en español
 * @param {string} cronExpression - Expresión cron (5 campos)
 * @returns {string} Descripción legible
 */
export const parseCronExpression = (cronExpression) => {
  if (!cronExpression) return "Sin programación";

  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length !== 5) return cronExpression;

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Casos comunes
  if (cronExpression === "0 9 1 1,4,7,10 *") {
    return "Trimestral: 1 de cada trimestre a las 09:00";
  }
  if (cronExpression === "0 9 1 * *") {
    return "Mensual: día 1 de cada mes a las 09:00";
  }
  if (cronExpression === "0 9 * * 1") {
    return "Semanal: cada lunes a las 09:00";
  }
  if (cronExpression === "0 9 * * *") {
    return "Diario: todos los días a las 09:00";
  }

  // Intentar parsear genérico
  let description = "";

  // Minutos y horas
  if (minute !== "*" && hour !== "*") {
    description += `a las ${hour}:${minute.padStart(2, "0")}`;
  } else if (hour !== "*") {
    description += `cada hora a las ${hour}:00`;
  }

  // Día del mes
  if (dayOfMonth !== "*") {
    if (dayOfMonth.includes(",")) {
      description += `, días ${dayOfMonth}`;
    } else if (dayOfMonth.includes("/")) {
      const interval = dayOfMonth.split("/")[1];
      description += `, cada ${interval} días`;
    } else {
      description += `, día ${dayOfMonth}`;
    }
  }

  // Mes
  if (month !== "*") {
    if (month.includes(",")) {
      const months = month.split(",");
      const monthNames = {
        1: "ene",
        4: "abr",
        7: "jul",
        10: "oct",
      };
      const names = months.map((m) => monthNames[m] || m).join(", ");
      description += ` en ${names}`;
    } else if (month.includes("/")) {
      const interval = month.split("/")[1];
      description += `, cada ${interval} meses`;
    }
  }

  return description || cronExpression;
};

/**
 * Determina el color del chip según el tiempo restante
 * @param {object} timeRemaining - Objeto retornado por getTimeRemaining
 * @returns {string} Color del chip: "error", "warning", "success", "info"
 */
export const getNextRunColor = (timeRemaining) => {
  if (!timeRemaining) return "default";
  if (timeRemaining.isPast) return "error";
  if (timeRemaining.days === 0 && timeRemaining.hours < 24) return "warning";
  if (timeRemaining.days < 7) return "info";
  return "success";
};
